// The one `track()` fan-out every event in the app should go through:
// PostHog gets the event for product analytics, and signals (abra `/ev`)
// gets it for lead scoring — one call site, two destinations, so no host
// component ever needs to know a vendor's SDK exists. Mounted inside both
// PostHogProvider and SignalProvider (see App.tsx).

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from "react";
import { usePostHog } from "posthog-js/react";

import { useSignal } from "context/SignalContext";

/** Every value an analytics property may carry. No objects, no answer
 * values — field refs and small scalars only. */
export type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined | string[]
>;

interface AnalyticsContextType {
  track(event: string, properties?: AnalyticsProperties): void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
);

function stripUndefined(
  properties: AnalyticsProperties,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value !== undefined) result[key] = value;
  }
  return result;
}

export const AnalyticsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const posthog = usePostHog();
  const { trackSignalEvent } = useSignal();

  const track = useCallback(
    (event: string, properties?: AnalyticsProperties) => {
      const clean = properties ? stripUndefined(properties) : undefined;
      posthog.capture(event, clean);
      trackSignalEvent(event, clean);
    },
    [posthog, trackSignalEvent],
  );

  const value = useMemo(() => ({ track }), [track]);

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

// Outside a provider (a component rendered on its own, without the app's
// shell) events go nowhere rather than throwing — the FormV2 renderer emits
// from anywhere it's mounted and must never take a page down over
// analytics.
const NO_ANALYTICS: AnalyticsContextType = { track: () => undefined };

export function useAnalytics(): AnalyticsContextType {
  return useContext(AnalyticsContext) ?? NO_ANALYTICS;
}
